/**
 * Shared helper for applying multi-paragraph suggestions in the TipTap editor.
 *
 * Used by both the inline Comment card and the EditorContainer feedback handler
 * to replace annotated text with a (possibly multi-paragraph) suggestion.
 */

import {
  countTextMatches,
  findTextMatchRange,
  stripOptionalEllipsisWrappers,
} from "~shared/textMatching";
import {
  normalizeManuscriptPassageForMatch,
} from "~shared/manuscriptPassageMatch";
import { adaptReplacementQuotes } from "~shared/adaptReplacementQuotes";
import {
  isLikelyParagraphBoundary,
  isMultiParagraphPlainText,
  preserveReplacementParagraphSpacing,
  replacedSegmentForSpacingInference,
  replacedSegmentUsesVisualBlankLines,
  selectionHasSpacerParagraphsInRange,
} from "~shared/preserveReplacementParagraphSpacing";
import { buildInlineJsonFromLines } from "src/lib/tiptapInlineBuilders";
import { findFirstTextRangeInDoc, pmRangeForReplacementInsert } from "../../../src/lib/agentManuscriptHighlight";
const CONTEXT_WINDOW_CHARS = 240;
const REWRITE_HINT_OFFSET_TOLERANCE = 120;
const SINGLE_PARAGRAPH_HTML_RE = /^<p>([\s\S]*)<\/p>$/i;
const BLOCK_LEVEL_HTML_RE = /<(?:p|h[1-6]|ul|ol|li|blockquote|pre|table|hr|div)\b/i;
const HAS_HTML_OR_ENTITIES_RE = /<[^>]+>|&[a-zA-Z#0-9]+;/;
const UNSUPPORTED_TIPTAP_RENDERED_HTML_RE = /<(?:hr|table|thead|tbody|tfoot|tr|td|th)\b/i;

export const UNDO_FAILURE_REASON = {
  DOCUMENT_CHANGED: "document_changed",
  MISSING_EDITOR: "missing_editor",
};

const buildTightManuscriptParagraphNodes = (text) => {
  const lines = String(text).replace(/\r\n/g, "\n").split("\n");
  const nodes = [];
  let currentLines = [];
  const flush = () => {
    if (currentLines.length === 0) return;
    nodes.push({
      type: "paragraph",
      content: buildInlineJsonFromLines(currentLines),
    });
    currentLines = [];
  };
  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    if (
      i > 0
      && currentLines.length > 0
      && isLikelyParagraphBoundary(currentLines[currentLines.length - 1], line)
    ) {
      flush();
    }
    currentLines.push(line);
  }
  flush();
  return nodes.length > 0 ? nodes : [{ type: "paragraph", content: [] }];
};

/**
 * Legacy-doc replacement path: when the replaced selection visually relied on
 * spacer empty paragraphs (margin:0 docs that predate the Paragraph Spacing
 * setting), the replacement must recreate them or it would collapse against
 * the surrounding text. Blank-line runs in the suggestion text map to spacer
 * paragraphs: `\n\n` ⇒ one spacer row, each extra `\n` ⇒ one more.
 * (Paste no longer manufactures spacers — this is replacement-only.)
 */
const buildSpacerPreservingParagraphNodes = (text) => {
  const normalized = String(text).replace(/\r\n/g, "\n");
  if (normalized.length === 0) return [{ type: "paragraph", content: [] }];

  const segments = normalized.split("\n");
  const nodes = [];
  let blankRun = 0;
  /** Non-empty split segments merged into one logical paragraph when separated by single `\n` only */
  let paraLines = [];

  const flushParagraph = () => {
    if (paraLines.length === 0) return;
    nodes.push({
      type: "paragraph",
      content: buildInlineJsonFromLines(paraLines),
    });
    paraLines = [];
  };

  const pushEmptyParagraphs = (count) => {
    for (let k = 0; k < count; k += 1) {
      nodes.push({ type: "paragraph", content: [] });
    }
  };

  for (let i = 0; i < segments.length; i += 1) {
    const segment = segments[i];
    if (segment.length === 0) {
      blankRun += 1;
      continue;
    }

    if (paraLines.length === 0) {
      // Leading blank lines before first text are ignored (matches historical behavior).
      if (nodes.length > 0 && blankRun > 0) {
        pushEmptyParagraphs(Math.max(0, blankRun - 1));
      }
      paraLines = [segment];
      blankRun = 0;
      continue;
    }

    if (blankRun === 0) {
      // Adjacent segments ("A","B") → one `\n` → soft break inside the paragraph.
      paraLines.push(segment);
      continue;
    }

    flushParagraph();
    pushEmptyParagraphs(Math.max(1, blankRun - 1));
    paraLines = [segment];
    blankRun = 0;
  }

  flushParagraph();

  if (blankRun > 0) {
    pushEmptyParagraphs(Math.max(0, blankRun - 1));
  }

  return nodes;
};

const buildMultiParagraphInsertContent = (cleanSuggestion, useVisualSpacers) => ({
  cleanSuggestion,
  isMultiParagraph: true,
  contentToInsert: useVisualSpacers
    ? buildSpacerPreservingParagraphNodes(cleanSuggestion)
    : buildTightManuscriptParagraphNodes(cleanSuggestion),
});

const buildInsertContent = (
  rawSuggestion,
  renderedHtml,
  { useVisualSpacers = false, preferRenderedBlockHtml = false } = {},
) => {
  const cleanSuggestion = String(rawSuggestion ?? "").replace(/\t\s*\t/g, "\t");
  const renderedSuggestionHtml = typeof renderedHtml === "string" ? renderedHtml.trim() : "";
  const isMultiParagraph = isMultiParagraphPlainText(cleanSuggestion);

  if (renderedSuggestionHtml) {
    if (UNSUPPORTED_TIPTAP_RENDERED_HTML_RE.test(renderedSuggestionHtml)) {
      return buildMultiParagraphInsertContent(cleanSuggestion, useVisualSpacers);
    }

    const paragraphMatch = renderedSuggestionHtml.match(SINGLE_PARAGRAPH_HTML_RE);
    const hasAdditionalBlockMarkup =
      /<\/p>\s*<p\b/i.test(renderedSuggestionHtml)
      || BLOCK_LEVEL_HTML_RE.test(paragraphMatch ? paragraphMatch[1] : renderedSuggestionHtml);
    if (paragraphMatch && !hasAdditionalBlockMarkup && !isMultiParagraph) {
      const inlineSuggestion = cleanSuggestion
        .replace(/\r?\n+/g, " ")
        .replace(/[ \t]+/g, " ")
        .trim();
      return {
        cleanSuggestion: inlineSuggestion,
        isMultiParagraph: false,
        contentToInsert: paragraphMatch[1],
      };
    }
    if (isMultiParagraph || hasAdditionalBlockMarkup) {
      if (preferRenderedBlockHtml) {
        return {
          cleanSuggestion,
          isMultiParagraph: true,
          contentToInsert: renderedSuggestionHtml,
        };
      }
      return buildMultiParagraphInsertContent(cleanSuggestion, useVisualSpacers);
    }
    return {
      cleanSuggestion,
      isMultiParagraph: true,
      contentToInsert: renderedSuggestionHtml,
    };
  }

  if (!isMultiParagraph) {
    const inlineSuggestion = cleanSuggestion
      .replace(/\r?\n+/g, " ")
      .replace(/[ \t]+/g, " ")
      .trim();
    return {
      cleanSuggestion: inlineSuggestion,
      isMultiParagraph,
      contentToInsert: inlineSuggestion,
    };
  }
  return buildMultiParagraphInsertContent(cleanSuggestion, useVisualSpacers);
};

export function buildManuscriptReplacementInsertContent(rawSuggestion, renderedHtml) {
  return buildInsertContent(rawSuggestion, renderedHtml, {
    useVisualSpacers: false,
    preferRenderedBlockHtml: typeof renderedHtml === "string" && renderedHtml.trim().length > 0,
  });
}

export function getInsertedSelectionLength(contentToInsert, fallbackText) {
  const fallback = String(fallbackText ?? "");
  if (Array.isArray(contentToInsert)) return fallback.length;
  const content = typeof contentToInsert === "string" ? contentToInsert : "";
  if (!HAS_HTML_OR_ENTITIES_RE.test(content)) return fallback.length;

  const renderedText = content
    .replace(/<\/p>\s*<p[^>]*>/gi, "\n\n")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, "\"")
    .replace(/&#39;|&apos;/gi, "'");

  return renderedText.length || fallback.length;
}

function shouldRetryInvalidRenderedHtml(error, contentToInsert) {
  return (
    typeof contentToInsert === "string"
    && HAS_HTML_OR_ENTITIES_RE.test(contentToInsert)
    && error instanceof Error
    && (
      error.message.includes("Invalid HTML content")
      || error.message.includes("Invalid element found")
    )
  );
}

/**
 * `insertContent` with block content (multi-paragraph suggestions) splits the
 * host paragraph when the replaced range starts at the paragraph's first text
 * position — a cursor at paragraph start, or a range covering the paragraph's
 * entire text. The left half of that split is an empty paragraph stranded
 * above the inserted prose. Detect that shape up front so the insert chain can
 * delete the artifact inside the same transaction (keeps undo atomic and keeps
 * the track-changes mark range aligned with the inserted text).
 */
function insertLeavesEmptySplitParagraphAbove(tiptap, range, isMultiParagraph) {
  if (!isMultiParagraph) return false;
  const doc = tiptap?.state?.doc;
  if (!doc || typeof doc.resolve !== "function" || !range || !Number.isFinite(range.from)) {
    return false;
  }
  try {
    const $from = doc.resolve(range.from);
    return (
      $from.depth === 1
      && $from.parentOffset === 0
      && $from.parent.isTextblock
      && $from.parent.content.size > 0
    );
  } catch {
    return false;
  }
}

function withEmptySplitParagraphCleanup(chain, tiptap, range, isMultiParagraph) {
  if (!insertLeavesEmptySplitParagraphAbove(tiptap, range, isMultiParagraph)) return chain;
  if (typeof chain?.command !== "function") return chain;
  return chain.command(({ tr }) => {
    const paragraphStart = range.from - 1;
    const node = paragraphStart >= 0 ? tr.doc.nodeAt(paragraphStart) : null;
    if (node?.isTextblock && node.content.size === 0) {
      tr.delete(paragraphStart, paragraphStart + node.nodeSize);
    }
    return true;
  });
}

function runSuggestionInsertWithPlainTextFallback(
  createChain,
  { cleanSuggestion, isMultiParagraph, contentToInsert, useVisualSpacers },
  { tiptap, range, transactionBoundary } = {},
) {
  const runInsert = (prepared) => {
    const docBeforeRun = tiptap?.state?.doc;
    let chain = withEmptySplitParagraphCleanup(
      createChain(prepared.contentToInsert),
      tiptap,
      range,
      prepared.isMultiParagraph,
    );
    if (!transactionBoundary) {
      // TipTap dispatches the accumulated transaction before run() reports
      // whether every command callback returned true. Use ProseMirror's
      // immutable document snapshot to distinguish a dispatched manuscript
      // change from a genuine no-op. Feedback uses transactionBoundary below,
      // where preventDispatch makes the run result authoritative.
      const success = chain.scrollIntoView().run();
      return success !== false || tiptap?.state?.doc !== docBeforeRun;
    }
    if (typeof chain?.command !== "function") return false;

    let proposedTransaction;
    let boundaryIsSafe = false;
    chain = chain
      .scrollIntoView()
      .command(({ tr }) => {
        proposedTransaction = tr;
        boundaryIsSafe = suggestionTransactionIsTargeted(
          tr,
          transactionBoundary,
          prepared.cleanSuggestion,
        );
        // TipTap dispatches a chain even when one command returns false.
        // Hold every validated Feedback chain until run() confirms that all
        // commands succeeded, then dispatch the captured transaction below.
        tr.setMeta("preventDispatch", true);
        return boundaryIsSafe;
      });

    const success = chain.run();
    if (success === false || !boundaryIsSafe || !proposedTransaction) return false;
    proposedTransaction.setMeta("preventDispatch", false);
    return transactionBoundary.dispatch(proposedTransaction) !== false;
  };

  try {
    const success = runInsert({ cleanSuggestion, isMultiParagraph, contentToInsert });
    if (success === false) return null;
    return { cleanSuggestion, isMultiParagraph, contentToInsert };
  } catch (error) {
    if (!shouldRetryInvalidRenderedHtml(error, contentToInsert)) {
      throw error;
    }

    const fallback = buildInsertContent(cleanSuggestion, "", { useVisualSpacers });
    const retrySuccess = runInsert(fallback);
    if (retrySuccess === false) return null;
    return fallback;
  }
}

function suggestionTransactionIsTargeted(
  transaction,
  { docBefore, from, to, annotationId },
  expectedSuggestion,
) {
  try {
    if (!transaction?.docChanged || !transaction.doc || !transaction.mapping) return false;
    if (!docBefore || typeof docBefore.textBetween !== "function") return false;

    const sizeBefore = docBefore.content?.size;
    const sizeAfter = transaction.doc.content?.size;
    if (!Number.isFinite(sizeBefore) || !Number.isFinite(sizeAfter)) return false;
    if (!Number.isFinite(from) || !Number.isFinite(to) || from < 0 || to < from || to > sizeBefore) {
      return false;
    }

    const mappedFrom = transaction.mapping.map(from, -1);
    const mappedTo = transaction.mapping.map(to, 1);
    if (
      !Number.isFinite(mappedFrom)
      || !Number.isFinite(mappedTo)
      || mappedFrom < 0
      || mappedTo < mappedFrom
      || mappedTo > sizeAfter
    ) {
      return false;
    }

    const textBetween = (doc, rangeFrom, rangeTo) => (
      doc.textBetween(rangeFrom, rangeTo, "\n", "\0")
    );
    const withoutOneTrailingBlockBoundary = (value) => (
      value.endsWith("\n") ? value.slice(0, -1) : value
    );
    const withoutOneLeadingBlockBoundary = (value) => (
      value.startsWith("\n") ? value.slice(1) : value
    );
    const prefixBefore = textBetween(docBefore, 0, from);
    const prefixAfter = textBetween(transaction.doc, 0, mappedFrom);
    if (
      withoutOneTrailingBlockBoundary(prefixAfter)
      !== withoutOneTrailingBlockBoundary(prefixBefore)
    ) {
      return false;
    }
    const suffixBefore = textBetween(docBefore, to, sizeBefore);
    const suffixAfter = textBetween(transaction.doc, mappedTo, sizeAfter);
    if (
      withoutOneLeadingBlockBoundary(suffixAfter)
      !== withoutOneLeadingBlockBoundary(suffixBefore)
    ) {
      return false;
    }

    if (annotationId && findAnnotationRanges(transaction.doc, annotationId).length > 0) {
      return false;
    }

    const replacementAfter = textBetween(transaction.doc, mappedFrom, mappedTo);
    return (
      normalizeManuscriptPassageForMatch(replacementAfter.replace(/\0/g, "\n"))
      === normalizeManuscriptPassageForMatch(expectedSuggestion)
    );
  } catch {
    return false;
  }
}

function dispatchSuggestionTransaction(tiptap, transaction) {
  if (!tiptap?.view || tiptap.view.isDestroyed || typeof tiptap.view.dispatch !== "function") {
    return false;
  }
  tiptap.view.dispatch(transaction);
  return true;
}

function getInsertionMarkType(tiptap) {
  return tiptap?.state?.schema?.marks?.insertion
    || tiptap?.state?.doc?.type?.schema?.marks?.insertion
    || tiptap?.state?.tr?.doc?.type?.schema?.marks?.insertion
    || null;
}

function getLegacyInsertedMarkType(tiptap) {
  return tiptap?.state?.schema?.marks?.inserted
    || tiptap?.state?.doc?.type?.schema?.marks?.inserted
    || tiptap?.state?.tr?.doc?.type?.schema?.marks?.inserted
    || null;
}

function applyTrackChangeInsertionMark(tiptap, from, to) {
  const insertionMarkType = getInsertionMarkType(tiptap);
  const tr = tiptap?.state?.tr;
  const dispatch = tiptap?.view?.dispatch;
  if (!insertionMarkType || !tr || typeof tr.addMark !== "function" || typeof dispatch !== "function") {
    return false;
  }

  const insertionMark = typeof insertionMarkType.create === "function"
    ? insertionMarkType.create()
    : insertionMarkType;
  const legacyInsertedMarkType = getLegacyInsertedMarkType(tiptap);
  try {
    if (legacyInsertedMarkType && typeof tr.removeMark === "function") {
      tr.removeMark(from, to, legacyInsertedMarkType);
    }
    tr.addMark(from, to, insertionMark);
    dispatch(tr);
    return true;
  } catch {
    return false;
  }
}

function splitAnnotationIds(annotationIdValue) {
  return String(annotationIdValue ?? "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
}

function annotationIdMatches(markAnnotationId, targetAnnotationId) {
  if (!targetAnnotationId) return false;
  return splitAnnotationIds(markAnnotationId).includes(targetAnnotationId);
}

/** Collapse whitespace and smart punctuation so neighbor/suggestion overlap detection still works across minor typographic drift. */
function normalizeForOverlapCompare(value) {
  return String(value ?? "")
    .replace(/\u00a0/g, " ")
    .replace(/\u2013|\u2014|\u2212/g, "-")
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201c\u201d]/g, "\"")
    .replace(/\u2026/g, "...")
    .replace(/\.{2,}/g, ".")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

function longestSuffixPrefixOverlap(leftText, rightText) {
  const max = Math.min(leftText.length, rightText.length);
  for (let size = max; size > 0; size -= 1) {
    const a = leftText.slice(-size);
    const b = rightText.slice(0, size);
    if (a === b || normalizeForOverlapCompare(a) === normalizeForOverlapCompare(b)) {
      return size;
    }
  }
  return 0;
}

function longestPrefixSuffixOverlap(leftText, rightText) {
  const max = Math.min(leftText.length, rightText.length);
  for (let size = max; size > 0; size -= 1) {
    const a = leftText.slice(0, size);
    const b = rightText.slice(-size);
    if (a === b || normalizeForOverlapCompare(a) === normalizeForOverlapCompare(b)) {
      return size;
    }
  }
  return 0;
}

function isWordChar(char) {
  return /[A-Za-z0-9_]/.test(String(char ?? ""));
}

function wouldTrimInsideWord(text, splitIndex) {
  if (splitIndex <= 0 || splitIndex >= text.length) return false;
  return isWordChar(text[splitIndex - 1]) && isWordChar(text[splitIndex]);
}

function getBoundaryChars(doc, range, docEnd) {
  const beforeStart = doc.textBetween(Math.max(1, range.from - 1), range.from, "", "\0");
  const atStart = doc.textBetween(range.from, Math.min(range.from + 1, docEnd), "", "\0");
  const beforeEnd = doc.textBetween(Math.max(1, range.to - 1), range.to, "", "\0");
  const afterEnd = doc.textBetween(range.to, Math.min(range.to + 1, docEnd), "", "\0");
  return { beforeStart, atStart, beforeEnd, afterEnd };
}

function trimSuggestionByNeighborContext(doc, range, suggestionText) {
  if (!doc || !range) return suggestionText;
  const raw = String(suggestionText ?? "");
  if (!raw) return raw;

  const leftContext = doc.textBetween(
    Math.max(1, range.from - CONTEXT_WINDOW_CHARS),
    range.from,
    " ",
    "\0",
  );
  const docEnd = doc.content?.size ?? Number.POSITIVE_INFINITY;
  const rightContext = doc.textBetween(
    range.to,
    Math.min(range.to + CONTEXT_WINDOW_CHARS, docEnd),
    " ",
    "\0",
  );
  const boundary = getBoundaryChars(doc, range, docEnd);

  let trimFromStart = longestSuffixPrefixOverlap(leftContext, raw);
  // Prevent chopping the first word of a suggestion unless the annotation itself starts mid-word.
  if (
    wouldTrimInsideWord(raw, trimFromStart)
    && !(isWordChar(boundary.beforeStart) && isWordChar(boundary.atStart))
  ) {
    trimFromStart = 0;
  }
  const afterLeftTrim = raw.slice(trimFromStart);
  let trimFromEnd = longestPrefixSuffixOverlap(rightContext, afterLeftTrim);
  // Prevent chopping the trailing word unless the annotation itself ends mid-word.
  const endSplitIndex = Math.max(0, afterLeftTrim.length - trimFromEnd);
  if (
    wouldTrimInsideWord(afterLeftTrim, endSplitIndex)
    && !(isWordChar(boundary.beforeEnd) && isWordChar(boundary.afterEnd))
  ) {
    trimFromEnd = 0;
  }
  const trimmed = afterLeftTrim.slice(0, Math.max(0, afterLeftTrim.length - trimFromEnd));

  return trimmed;
}

function findAnnotationRanges(doc, annotationId) {
  const ranges = [];
  if (!doc || !annotationId) return ranges;
  doc.descendants((node, pos) => {
    node.marks.forEach((mark) => {
      if (mark.type.name === "annotation" && annotationIdMatches(mark.attrs.annotationId, annotationId)) {
        ranges.push({ from: pos, to: pos + node.nodeSize });
      }
    });
  });
  return ranges;
}

function getRangeText(doc, range) {
  if (!doc || !range) return "";
  return doc.textBetween(range.from, range.to, " ").trim();
}

function hasTextBetweenRanges(doc, leftRange, rightRange) {
  if (!doc || !leftRange || !rightRange) return false;
  if (rightRange.from <= leftRange.to) return false;
  const between = doc.textBetween(leftRange.to, rightRange.from, "", "\0");
  return between.trim().length > 0;
}

function mergeContiguousAnnotationRanges(doc, ranges) {
  if (!ranges.length) return null;
  const acceptedRanges = [ranges[0]];
  for (let i = 1; i < ranges.length; i++) {
    const previousRange = acceptedRanges[acceptedRanges.length - 1];
    if (hasTextBetweenRanges(doc, previousRange, ranges[i])) {
      return null;
    }
    acceptedRanges.push(ranges[i]);
  }
  const mergedRange = {
    from: acceptedRanges[0].from,
    to: acceptedRanges[acceptedRanges.length - 1].to,
  };
  return {
    ...mergedRange,
    text: getRangeText(doc, mergedRange),
  };
}

function splitAnnotationRangesIntoContiguousGroups(doc, ranges) {
  if (!ranges.length) return [];
  const groups = [];
  let currentGroup = [ranges[0]];
  for (let i = 1; i < ranges.length; i++) {
    const previousRange = currentGroup[currentGroup.length - 1];
    if (hasTextBetweenRanges(doc, previousRange, ranges[i])) {
      groups.push(currentGroup);
      currentGroup = [ranges[i]];
      continue;
    }
    currentGroup.push(ranges[i]);
  }
  groups.push(currentGroup);
  return groups;
}

/** True when any annotation mark with this id is present (even if split/ambiguous). */
export function hasAnnotationMarksInEditor(tiptap, annotationId) {
  if (!tiptap?.state?.doc || !annotationId) return false;
  return findAnnotationRanges(tiptap.state.doc, annotationId).length > 0;
}

/**
 * Locate an annotation mark range in the editor.
 * Contiguous spans (including structural paragraph gaps with no text) merge as one range.
 * When the same annotation id is split across real manuscript text, optionally resolve via
 * preferredText (e.g. comment.original) to the uniquely matching contiguous group.
 *
 * @param {Object} tiptap
 * @param {string} annotationId
 * @param {Object} [opts]
 * @param {string} [opts.preferredText]
 */
export function getAnnotationRangeInEditor(tiptap, annotationId, { preferredText } = {}) {
  if (!tiptap?.state?.doc || !annotationId) return null;
  const doc = tiptap.state.doc;
  const ranges = findAnnotationRanges(doc, annotationId);
  if (ranges.length === 0) return null;
  ranges.sort((a, b) => a.from - b.from);

  const contiguous = mergeContiguousAnnotationRanges(doc, ranges);
  if (contiguous) {
    const preferred = String(preferredText ?? "").trim();
    if (preferred && !passageMatchesDocRangeTextStrict(contiguous.text, preferred)) {
      return null;
    }
    return contiguous;
  }

  const preferred = String(preferredText ?? "").trim();
  if (!preferred) return null;

  // Prefer strict equality (whitespace-agnostic) over substring/prefix overlap.
  // Loose overlap can uniquely "match" a short remnant span inside comment.original
  // and then replace that fragment with the full suggestion.
  const matchingGroups = splitAnnotationRangesIntoContiguousGroups(doc, ranges)
    .map((group) => mergeContiguousAnnotationRanges(doc, group))
    .filter(Boolean)
    .filter((group) => passageMatchesDocRangeTextStrict(group.text, preferred));

  if (matchingGroups.length === 1) return matchingGroups[0];
  return null;
}

/**
 * Find annotation ranges, build the replacement content, and execute the
 * TipTap chain that performs the atomic text replacement.
 *
 * @param {Object} tiptap       – TipTap editor instance
 * @param {string} suggestion   – Raw suggestion text (may contain \n\n for paragraphs)
 * @param {Object} opts
 * @param {string} opts.annotationId – Annotation ID to locate + remove
 * @param {string} [opts.renderedHtml] – Optional rendered HTML for markdown-aware insertion
 * @param {string} [opts.preferredText] – Disambiguate split annotation marks (e.g. comment.original)
 * @param {boolean} [opts.validateTargetBoundary] – Reject before dispatch if the chain changes text outside the target
 * @returns {boolean} true if the replacement was applied, false otherwise
 */
export function applySuggestionToEditor(
  tiptap,
  suggestion,
  { annotationId, renderedHtml, skipInsertionMark, preferredText, validateTargetBoundary = false } = {},
) {
  const annotationRange = getAnnotationRangeInEditor(tiptap, annotationId, { preferredText });
  if (!annotationRange) return false;
  const { from, to } = annotationRange;
  const replaceRange = { from, to };
  const docBefore = tiptap.state.doc;

  const prepared = prepareSuggestionInsert(
    tiptap,
    { from, to },
    suggestion,
    renderedHtml,
  );

  // Execute atomic replacement
  const inserted = runSuggestionInsertWithPlainTextFallback(
    (insertContent) => tiptap
      .chain()
      .focus()
      .setTextSelection({ from, to })
      .unsetAnnotation(annotationId)
      .insertContent(insertContent),
    prepared,
    {
      tiptap,
      range: { from, to },
      transactionBoundary: validateTargetBoundary
        ? {
          docBefore,
          from,
          to,
          annotationId,
          dispatch: (transaction) => dispatchSuggestionTransaction(tiptap, transaction),
        }
        : undefined,
    },
  );
  if (!inserted) return false;
  if (!skipInsertionMark) {
    markInsertedSuggestionRange(tiptap, {
      insertFrom: from,
      replaceRange,
      docBefore,
      cleanSuggestion: inserted.cleanSuggestion,
      isMultiParagraph: inserted.isMultiParagraph,
      contentToInsert: inserted.contentToInsert,
    });
  }
  return true;
}

/**
 * Replace the first exact passage match in the editor with the suggestion.
 * Intended for chat-based rewrite apply where annotation IDs may be unavailable.
 */
function buildPlainTextWithCharPositions(doc) {
  const charPositions = [];
  let plain = "";

  if (typeof doc.forEach === "function") {
    let isFirstBlock = true;

    doc.forEach((node, offset) => {
      if (!node.isTextblock) return;
      if (!isFirstBlock) {
        plain += "\n";
        charPositions.push(offset);
      }
      isFirstBlock = false;

      // `node.descendants` reports positions relative to the block node;
      // block content starts at `offset + 1` in document coordinates.
      node.descendants((child, pos) => {
        if (!child?.isText || typeof child.text !== "string") return;
        for (let i = 0; i < child.text.length; i += 1) {
          charPositions.push(offset + 1 + pos + i);
          plain += child.text[i];
        }
      });
    });

    return { plain, charPositions };
  }

  doc.descendants((node, pos) => {
    if (!node?.isText || typeof node.text !== "string") return;
    for (let i = 0; i < node.text.length; i += 1) {
      charPositions.push(pos + i);
      plain += node.text[i];
    }
  });

  return { plain, charPositions };
}

function passageMatchesDocRangeTextStrict(rangeText, passage) {
  const range = String(rangeText ?? "").trim();
  const target = String(passage ?? "").trim();
  if (!range || !target) return false;
  if (normalizeManuscriptPassageForMatch(range) === normalizeManuscriptPassageForMatch(target)) {
    return true;
  }

  const matchesFullRange = (candidate) => {
    const matchInRange = findTextMatchRange(range, candidate, { mode: "whitespace_agnostic" });
    if (matchInRange?.start === 0 && matchInRange.end === range.length) return true;

    const matchInCandidate = findTextMatchRange(candidate, range, { mode: "whitespace_agnostic" });
    return matchInCandidate?.start === 0 && matchInCandidate.end === candidate.length;
  };

  if (matchesFullRange(target)) return true;

  const unwrappedTarget = stripOptionalEllipsisWrappers(target);
  return unwrappedTarget !== target && matchesFullRange(unwrappedTarget);
}

export function passagesAlignForStoredHint(rangeText, passage) {
  if (passageMatchesDocRangeTextStrict(rangeText, passage)) return true;

  const range = String(rangeText ?? "").trim();
  const target = String(passage ?? "").trim();
  if (!range) return false;
  if (!target) return true;

  const rangeNorm = normalizeForOverlapCompare(range);
  const targetNorm = normalizeForOverlapCompare(target);
  if (!rangeNorm || !targetNorm) return false;
  if (rangeNorm === targetNorm) return true;
  if (rangeNorm.includes(targetNorm) || targetNorm.includes(rangeNorm)) return true;

  const minLen = Math.min(rangeNorm.length, targetNorm.length);
  if (minLen >= 12) {
    const prefixLen = Math.min(24, minLen);
    if (rangeNorm.slice(0, prefixLen) === targetNorm.slice(0, prefixLen)) return true;
  }

  return false;
}

export function passageMatchesDocRangeText(rangeText, passage) {
  return passagesAlignForStoredHint(rangeText, passage);
}

function resolvePassageRangeFromEditorHint(doc, passage, editorSelectionHint) {
  if (!doc || !editorSelectionHint || !passage) return null;
  const pmFrom = Number(editorSelectionHint.pmFrom);
  const pmTo = Number(editorSelectionHint.pmTo);
  if (!Number.isFinite(pmFrom) || !Number.isFinite(pmTo) || pmTo <= pmFrom) return null;

  try {
    const rangeText = doc.textBetween(pmFrom, pmTo, "\n", "\0");
    if (!passagesAlignForStoredHint(rangeText, passage)) return null;
    return { from: pmFrom, to: pmTo };
  } catch {
    return null;
  }
}

function editorSelectionHintMatchesPassage(doc, passage, editorSelectionHint) {
  return Boolean(resolvePassageRangeFromEditorHint(doc, passage, editorSelectionHint));
}

function prepareSuggestionInsert(tiptap, range, suggestion, renderedHtml) {
  let suggestionForBuild = suggestion;
  let useVisualSpacers = false;
  if (tiptap?.state?.doc && range) {
    const replacedSegment = replacedSegmentForSpacingInference(tiptap.state.doc, range.from, range.to);
    useVisualSpacers =
      selectionHasSpacerParagraphsInRange(tiptap.state.doc, range.from, range.to)
      || replacedSegmentUsesVisualBlankLines(replacedSegment);
    suggestionForBuild = preserveReplacementParagraphSpacing(String(suggestion ?? ""), replacedSegment);
  }
  const built = buildInsertContent(suggestionForBuild, renderedHtml, {
    useVisualSpacers,
    // When replacing manuscript ranges that visibly rely on spacer paragraphs,
    // preserve spacer-aware paragraph insertion instead of raw rendered HTML.
    preferRenderedBlockHtml:
      typeof renderedHtml === "string"
      && renderedHtml.trim().length > 0
      && !useVisualSpacers,
  });
  let { cleanSuggestion, isMultiParagraph, contentToInsert } = built;

  if (!isMultiParagraph && tiptap?.state?.doc && range) {
    const docEnd = tiptap.state.doc.content?.size ?? range.to;
    const leftContext = tiptap.state.doc.textBetween(
      Math.max(1, range.from - CONTEXT_WINDOW_CHARS),
      range.from,
      " ",
      "\0",
    );
    const rightContext = tiptap.state.doc.textBetween(
      range.to,
      Math.min(range.to + CONTEXT_WINDOW_CHARS, docEnd),
      " ",
      "\0",
    );
    const replacedSegment = tiptap.state.doc.textBetween(range.from, range.to, "\n", "\0");
    cleanSuggestion = adaptReplacementQuotes({
      newText: cleanSuggestion,
      replaced: replacedSegment,
      left: leftContext,
      right: rightContext,
    });
    if (cleanSuggestion !== built.cleanSuggestion) {
      const rebuilt = buildInsertContent(cleanSuggestion, "", { useVisualSpacers });
      cleanSuggestion = rebuilt.cleanSuggestion;
      contentToInsert = rebuilt.contentToInsert;
      isMultiParagraph = rebuilt.isMultiParagraph;
    }
  }

  if (!isMultiParagraph) {
    const trimmedPlain = trimSuggestionByNeighborContext(tiptap.state.doc, range, cleanSuggestion);
    if (trimmedPlain !== cleanSuggestion) {
      const rebuilt = buildInsertContent(trimmedPlain, "", { useVisualSpacers });
      cleanSuggestion = rebuilt.cleanSuggestion;
      contentToInsert = rebuilt.contentToInsert;
      isMultiParagraph = rebuilt.isMultiParagraph;
    }
  }

  return { cleanSuggestion, isMultiParagraph, contentToInsert, useVisualSpacers };
}

/**
 * Marks freshly inserted suggestion prose as `inserted` so the editor shows
 * purple track-changes styling for chat/carousel applies.
 */
function markInsertedSuggestionRange(tiptap, {
  insertFrom,
  cleanSuggestion,
  isMultiParagraph,
  contentToInsert,
  replaceRange,
  docBefore,
}) {
  if (!tiptap?.chain || !Number.isFinite(insertFrom) || insertFrom < 1) return;

  let postInsertRange = null;
  if (docBefore && replaceRange && tiptap.state?.doc?.content?.size !== docBefore.content?.size) {
    postInsertRange = pmRangeForReplacementInsert(docBefore, tiptap.state?.doc, replaceRange);
  }
  if (!postInsertRange) {
    postInsertRange = findFirstTextRangeInDoc(tiptap.state?.doc, cleanSuggestion);
  }
  const lengthDerivedInsertTo = insertFrom + getInsertedSelectionLength(contentToInsert, cleanSuggestion);
  const hasResolvedRange = Boolean(
    postInsertRange
    && postInsertRange.from >= insertFrom - 2
    && postInsertRange.to > postInsertRange.from,
  );
  const markFrom = hasResolvedRange ? postInsertRange.from : insertFrom;
  const insertTo = hasResolvedRange ? postInsertRange.to : lengthDerivedInsertTo;
  if (!Number.isFinite(insertTo) || insertTo <= markFrom) return;

  if (applyTrackChangeInsertionMark(tiptap, markFrom, insertTo)) {
    if (typeof tiptap?.chain === "function") {
      try {
        tiptap.chain().focus().setTextSelection({ from: insertTo, to: insertTo }).scrollIntoView().run();
      } catch {
        // Collapsing selection after track-change marking is best-effort.
      }
    }
    return;
  }

  let chain = tiptap.chain().focus();
  if (typeof chain.setTextSelection === "function") {
    chain = chain.setTextSelection({ from: markFrom, to: insertTo }) || chain;
  }
  if (typeof chain.setInserted === "function") {
    chain = chain.setInserted() || chain;
  }
  if (typeof chain.setTextSelection === "function") {
    chain = chain.setTextSelection({ from: insertTo, to: insertTo }) || chain;
  }
  if (typeof chain.scrollIntoView === "function") {
    chain = chain.scrollIntoView() || chain;
  }
  if (typeof chain.run === "function") {
    try {
      chain.run();
    } catch {
      // Track-change marking is visual only; a failed mark should not undo the applied rewrite.
    }
  }
}

function passageMatchesPmSelectionText(pmText, target) {
  return passagesAlignForStoredHint(pmText, target);
}

function resolveAuthoritativeEditorHintRange(doc, passage, editorSelectionHint) {
  if (!doc || !editorSelectionHint) return null;
  const pmFrom = Number(editorSelectionHint.pmFrom);
  const pmTo = Number(editorSelectionHint.pmTo);
  if (!Number.isFinite(pmFrom) || !Number.isFinite(pmTo) || pmTo <= pmFrom) return null;

  try {
    const size = doc.content?.size ?? pmTo;
    const from = Math.max(1, Math.min(Math.floor(pmFrom), size));
    const to = Math.max(from, Math.min(Math.floor(pmTo), size));
    const target = String(passage ?? "").trim();
    const pmText = doc.textBetween(from, to, "\n", "\0");
    if (!target) {
      return pmText.trim() ? { from, to } : null;
    }
    if (passagesAlignForStoredHint(pmText, target)) return { from, to };
    return null;
  } catch {
    return null;
  }
}

function findPassageMatchRange(doc, passage, options = {}) {
  const target = String(passage ?? "").trim();
  if (!target) return null;

  const authoritative = resolveAuthoritativeEditorHintRange(doc, target, options.editorSelectionHint);
  if (authoritative) return authoritative;

  const hintedRange = resolvePassageRangeFromEditorHint(doc, target, options.editorSelectionHint);
  if (hintedRange) return hintedRange;

  const { plain, charPositions } = buildPlainTextWithCharPositions(doc);
  const hintOffset = (() => {
    const hint = options.editorSelectionHint;
    if (
      hint
      && Number.isFinite(hint.plainTextStart)
      && editorSelectionHintMatchesPassage(doc, target, hint)
    ) {
      return hint.plainTextStart;
    }
    if (Number.isFinite(options.hintOffset)) {
      return options.hintOffset;
    }
    return undefined;
  })();

  const plainRangeNearHint = (range) => {
    if (!range || hintOffset === undefined) return true;
    return Math.abs(range.start - hintOffset) <= REWRITE_HINT_OFFSET_TOLERANCE;
  };

  const plainRangeToDocRange = (range) => {
    if (!range) return null;
    const { start, end } = range;
    if (start < 0 || end <= start) return null;
    if (end - 1 >= charPositions.length) return null;
    const from = charPositions[start];
    const to = charPositions[end - 1] + 1;
    if (from == null || to == null) return null;
    return { from, to };
  };

  const findSentenceRangeForRelativeTarget = () => {
    const normalizedTarget = target.toLowerCase().replace(/\s+/g, " ").trim();
    const wantsLastSentence = /\b(last|final)\s+sentence\b/.test(normalizedTarget);
    const wantsFirstSentence = /\b(first|opening|initial)\s+sentence\b/.test(normalizedTarget);
    if (!wantsLastSentence && !wantsFirstSentence) return null;

    const sentenceRanges = [];
    let segmentStart = 0;

    const pushSegment = (start, end) => {
      let trimmedStart = start;
      let trimmedEnd = end;
      while (trimmedStart < trimmedEnd && /\s/.test(plain[trimmedStart])) trimmedStart += 1;
      while (trimmedEnd > trimmedStart && /\s/.test(plain[trimmedEnd - 1])) trimmedEnd -= 1;
      if (trimmedEnd > trimmedStart) {
        sentenceRanges.push({ start: trimmedStart, end: trimmedEnd });
      }
    };

    for (let i = 0; i < plain.length; i += 1) {
      if (!/[.!?]/.test(plain[i])) continue;
      let end = i + 1;
      while (end < plain.length && /["')\]”’]/.test(plain[end])) end += 1;
      pushSegment(segmentStart, end);
      segmentStart = end;
    }

    if (segmentStart < plain.length) {
      pushSegment(segmentStart, plain.length);
    }

    if (sentenceRanges.length === 0) return null;
    return wantsLastSentence ? sentenceRanges[sentenceRanges.length - 1] : sentenceRanges[0];
  };

  const exactMatch = findTextMatchRange(plain, target, { mode: "exact", hintOffset });
  const exactRange = plainRangeNearHint(exactMatch) ? plainRangeToDocRange(exactMatch) : null;
  if (exactRange) {
    return exactRange;
  }

  const normalizedRange = plainRangeToDocRange(findTextMatchRange(plain, target, { mode: "normalized", hintOffset }));
  if (normalizedRange) return normalizedRange;
  const whitespaceAgnosticRange = plainRangeToDocRange(
    findTextMatchRange(plain, target, { mode: "whitespace_agnostic", hintOffset }),
  );
  if (whitespaceAgnosticRange) return whitespaceAgnosticRange;

  return plainRangeToDocRange(findSentenceRangeForRelativeTarget());
}

/**
 * True when locating `passage` in `doc` would replace essentially all manuscript text.
 * Non-empty documents should not be overwritten wholesale from carousel / chat apply.
 */
export function passageMatchWouldReplaceEntireDocument(doc, passage) {
  if (!doc || typeof doc.textBetween !== "function") return false;
  const matchRange = findPassageMatchRange(doc, passage);
  if (!matchRange) return false;

  let docText = "";
  try {
    const size = doc.content?.size ?? 0;
    if (size <= 0) return false;
    docText = doc.textBetween(0, size, "\n", "\n").trim();
  } catch {
    return false;
  }
  if (!docText) return false;

  const matchedText = doc.textBetween(matchRange.from, matchRange.to, "\n", "\n").trim();
  if (!matchedText) return false;

  return matchedText.length >= docText.length * 0.9;
}

function passageMatchIsUniqueInPlainText(doc, passage) {
  if (!doc || !passage) return false;
  const { plain } = buildPlainTextWithCharPositions(doc);
  const target = String(passage ?? "").trim();
  if (!plain.trim() || !target) return false;

  for (const mode of ["exact", "normalized", "whitespace_agnostic"]) {
    const count = countTextMatches(plain, target, { mode });
    if (count > 1) return false;
    if (count === 1) return true;
  }
  return false;
}

/**
 * Missing annotation recovery is only safe for a uniquely matched source passage.
 * Multi-paragraph originals are allowed when they uniquely locate a local range —
 * Developmental Feedback commonly stores multi-sentence originals, and blocking those
 * forced users to re-run Feedback (burning credits) after annotation marks drifted.
 * Ambiguous/duplicated passages and whole-document matches still fail closed.
 */
export function feedbackOriginalFallbackTargetIsSafe(tiptap, originalPassage) {
  const doc = tiptap?.state?.doc;
  const passage = String(originalPassage ?? "").trim();
  if (!doc || !passage) return false;
  if (passageMatchWouldReplaceEntireDocument(doc, passage)) return false;
  if (!passageMatchIsUniqueInPlainText(doc, passage)) return false;
  return Boolean(findPassageMatchRange(doc, passage));
}

/**
 * Build stable ProseMirror positions for a highlighted passage.
 * Prefers the live selection when it still matches; otherwise searches the document.
 *
 * @param {Object} editor – TipTap editor instance
 * @param {Object} opts
 * @param {string} opts.documentId
 * @param {string} opts.passage
 * @param {Object} [opts.preferredHint] – Prior hint used to disambiguate duplicate matches
 */
export function buildEditorSelectionHintForPassage(editor, { documentId, passage, preferredHint } = {}) {
  if (!editor?.state?.doc || !documentId) return undefined;
  const doc = editor.state.doc;
  const target = String(passage ?? "").trim();
  if (!target) return undefined;

  try {
    const { from: pmFrom, to: pmTo } = editor.state.selection;
    if (pmTo > pmFrom && passageMatchesDocRangeText(doc.textBetween(pmFrom, pmTo, "\n", "\0"), target)) {
      return {
        documentId,
        pmFrom,
        pmTo,
        plainTextStart: doc.textBetween(0, pmFrom, "\n").length,
        plainTextEnd: doc.textBetween(0, pmTo, "\n").length,
      };
    }
  } catch {
    // Fall through to document search.
  }

  if (
    preferredHint
    && Number.isFinite(preferredHint.pmFrom)
    && Number.isFinite(preferredHint.pmTo)
    && preferredHint.pmTo > preferredHint.pmFrom
  ) {
    const hintedRange = resolvePassageRangeFromEditorHint(doc, target, preferredHint);
    if (hintedRange) {
      return {
        documentId,
        pmFrom: hintedRange.from,
        pmTo: hintedRange.to,
        plainTextStart: doc.textBetween(0, hintedRange.from, "\n").length,
        plainTextEnd: doc.textBetween(0, hintedRange.to, "\n").length,
      };
    }
  }

  const hintOffset = Number.isFinite(preferredHint?.plainTextStart)
    ? preferredHint.plainTextStart
    : undefined;

  const matchRange = findPassageMatchRange(doc, target, {
    editorSelectionHint: preferredHint,
    hintOffset,
  });
  if (!matchRange) {
    if (
      preferredHint
      && Number.isFinite(preferredHint.pmFrom)
      && Number.isFinite(preferredHint.pmTo)
      && preferredHint.pmTo > preferredHint.pmFrom
    ) {
      try {
        const pmText = doc.textBetween(preferredHint.pmFrom, preferredHint.pmTo, "\n", "\0");
        if (passagesAlignForStoredHint(pmText, target)) {
          return {
            documentId,
            pmFrom: preferredHint.pmFrom,
            pmTo: preferredHint.pmTo,
            plainTextStart: preferredHint.plainTextStart,
            plainTextEnd: preferredHint.plainTextEnd,
          };
        }
      } catch {
        // Ignore and fall through.
      }
    }
    return undefined;
  }

  const rebuilt = {
    documentId,
    pmFrom: matchRange.from,
    pmTo: matchRange.to,
    plainTextStart: doc.textBetween(0, matchRange.from, "\n").length,
    plainTextEnd: doc.textBetween(0, matchRange.to, "\n").length,
  };

  if (
    preferredHint
    && Number.isFinite(preferredHint.plainTextStart)
    && Math.abs(rebuilt.plainTextStart - preferredHint.plainTextStart) > REWRITE_HINT_OFFSET_TOLERANCE
    && Number.isFinite(preferredHint.pmFrom)
    && Number.isFinite(preferredHint.pmTo)
    && preferredHint.pmTo > preferredHint.pmFrom
  ) {
    try {
      const pmText = doc.textBetween(preferredHint.pmFrom, preferredHint.pmTo, "\n", "\0");
      if (passagesAlignForStoredHint(pmText, target)) {
        return {
          documentId,
          pmFrom: preferredHint.pmFrom,
          pmTo: preferredHint.pmTo,
          plainTextStart: preferredHint.plainTextStart,
          plainTextEnd: preferredHint.plainTextEnd,
        };
      }
    } catch {
      // Keep rebuilt search result.
    }
  }

  return rebuilt;
}

/**
 * @param {Object} opts
 * @param {string} opts.passage
 * @param {string} [opts.renderedHtml] – Optional rendered HTML for markdown-aware insertion
 * @param {Object} [opts.editorSelectionHint] – ProseMirror positions captured when the user sent the message
 */
export function applySuggestionToPassageInEditor(
  tiptap,
  suggestion,
  { passage, renderedHtml, editorSelectionHint, skipInsertionMark, validateTargetBoundary = false } = {},
) {
  const hintOffset = Number.isFinite(editorSelectionHint?.plainTextStart)
    ? editorSelectionHint.plainTextStart
    : undefined;
  const matchRange = findPassageMatchRange(tiptap.state.doc, passage, { editorSelectionHint, hintOffset });
  if (!matchRange) return false;
  const docBefore = tiptap.state.doc;

  const prepared = prepareSuggestionInsert(
    tiptap,
    matchRange,
    suggestion,
    renderedHtml,
  );

  const inserted = runSuggestionInsertWithPlainTextFallback(
    (insertContent) => tiptap
      .chain()
      .focus()
      .setTextSelection(matchRange)
      .insertContent(insertContent),
    prepared,
    {
      tiptap,
      range: matchRange,
      transactionBoundary: validateTargetBoundary
        ? {
          docBefore,
          from: matchRange.from,
          to: matchRange.to,
          dispatch: (transaction) => dispatchSuggestionTransaction(tiptap, transaction),
        }
        : undefined,
    },
  );
  if (!inserted) return false;
  if (!skipInsertionMark) {
    markInsertedSuggestionRange(tiptap, {
      insertFrom: matchRange.from,
      replaceRange: matchRange,
      docBefore,
      cleanSuggestion: inserted.cleanSuggestion,
      isMultiParagraph: inserted.isMultiParagraph,
      contentToInsert: inserted.contentToInsert,
    });
  }
  return true;
}

export function isEditorDocumentEffectivelyEmpty(tiptap) {
  const doc = tiptap?.state?.doc;
  if (!doc || typeof doc.textBetween !== "function") return false;
  try {
    const size = doc.content?.size ?? 0;
    if (size <= 0) return false;
    return doc.textBetween(0, size, "\n", "\n").trim().length === 0;
  } catch {
    return false;
  }
}

/**
 * Inserts carousel / rewrite suggestion prose into a manuscript with no text yet.
 */
export function applySuggestionToEmptyDocument(tiptap, suggestion, { renderedHtml, skipInsertionMark } = {}) {
  if (!isEditorDocumentEffectivelyEmpty(tiptap)) return false;

  const insertPos = 1;
  const range = { from: insertPos, to: insertPos };
  const docBefore = tiptap.state.doc;
  const prepared = prepareSuggestionInsert(
    tiptap,
    range,
    suggestion,
    renderedHtml,
  );

  const inserted = runSuggestionInsertWithPlainTextFallback(
    (insertContent) => tiptap
      .chain()
      .focus()
      .setTextSelection(range)
      .insertContent(insertContent),
    prepared,
    { tiptap, range },
  );
  if (!inserted) return false;
  if (!skipInsertionMark) {
    markInsertedSuggestionRange(tiptap, {
      insertFrom: insertPos,
      replaceRange: range,
      docBefore,
      cleanSuggestion: inserted.cleanSuggestion,
      isMultiParagraph: inserted.isMultiParagraph,
      contentToInsert: inserted.contentToInsert,
    });
  }
  return true;
}

export function applySuggestionToSelectionInEditor(tiptap, suggestion, { renderedHtml, skipInsertionMark } = {}) {
  const selection = tiptap?.state?.selection;
  if (!selection) return false;
  const from = Math.min(selection.from ?? 0, selection.to ?? 0);
  const to = Math.max(selection.from ?? 0, selection.to ?? 0);
  if (from <= 0 || to <= from) return false;
  const replaceRange = { from, to };
  const docBefore = tiptap.state.doc;

  const prepared = prepareSuggestionInsert(
    tiptap,
    { from, to },
    suggestion,
    renderedHtml,
  );

  const inserted = runSuggestionInsertWithPlainTextFallback(
    (insertContent) => tiptap
      .chain()
      .focus()
      .setTextSelection({ from, to })
      .insertContent(insertContent),
    prepared,
    { tiptap, range: { from, to } },
  );
  if (!inserted) return false;
  if (!skipInsertionMark) {
    markInsertedSuggestionRange(tiptap, {
      insertFrom: from,
      replaceRange,
      docBefore,
      cleanSuggestion: inserted.cleanSuggestion,
      isMultiParagraph: inserted.isMultiParagraph,
      contentToInsert: inserted.contentToInsert,
    });
  }
  return true;
}

/**
 * Inserts carousel / rewrite suggestion prose at the editor cursor when there is no highlight.
 */
export function applySuggestionAtCursorInEditor(tiptap, suggestion, { renderedHtml, skipInsertionMark } = {}) {
  const selection = tiptap?.state?.selection;
  if (!selection) return false;
  const from = selection.from ?? 0;
  const to = selection.to ?? from;
  if (from !== to || from < 1) return false;

  const range = { from, to: from };
  const docBefore = tiptap.state.doc;
  const prepared = prepareSuggestionInsert(
    tiptap,
    range,
    suggestion,
    renderedHtml,
  );

  const inserted = runSuggestionInsertWithPlainTextFallback(
    (insertContent) => tiptap
      .chain()
      .focus()
      .setTextSelection(range)
      .insertContent(insertContent),
    prepared,
    { tiptap, range },
  );
  if (!inserted) return false;
  if (!skipInsertionMark) {
    markInsertedSuggestionRange(tiptap, {
      insertFrom: from,
      replaceRange: range,
      docBefore,
      cleanSuggestion: inserted.cleanSuggestion,
      isMultiParagraph: inserted.isMultiParagraph,
      contentToInsert: inserted.contentToInsert,
    });
  }
  return true;
}

/**
 * When switching carousel options, replace another suggestion from the same card set
 * that is already present in the manuscript. Skips peers that would overwrite the
 * entire document — non-empty docs should only get partial replacements or cursor insert.
 */
export function applySuggestionReplacingSiblingCarouselOption(
  tiptap,
  suggestion,
  siblingSuggestions,
  { renderedHtml, editorSelectionHint } = {},
) {
  if (!Array.isArray(siblingSuggestions) || siblingSuggestions.length < 2) return false;
  if (isEditorDocumentEffectivelyEmpty(tiptap)) return false;
  const normalizedSuggestion = String(suggestion ?? "").trim();
  const peers = siblingSuggestions
    .filter((peer) => typeof peer === "string" && peer.trim() && peer.trim() !== normalizedSuggestion)
    .sort((a, b) => b.length - a.length);

  for (const peer of peers) {
    if (passageMatchWouldReplaceEntireDocument(tiptap.state.doc, peer)) continue;
    if (applySuggestionToPassageInEditor(tiptap, suggestion, { passage: peer, renderedHtml, editorSelectionHint })) {
      return true;
    }
  }
  return false;
}

export function setAnnotationOnPassageInEditor(tiptap, annotationId, { passage }) {
  if (!tiptap?.state?.doc || !annotationId) return false;
  const matchRange = findPassageMatchRange(tiptap.state.doc, passage);
  if (!matchRange) return false;
  tiptap
    .chain()
    .focus()
    .setTextSelection(matchRange)
    .setAnnotation({ annotationId })
    .setTextSelection({ from: matchRange.to, to: matchRange.to })
    .scrollIntoView()
    .run();
  return true;
}

function reopenUsingPassages(tiptap, annotationId, passageCandidates) {
  for (const passage of passageCandidates) {
    if (!passage || String(passage).trim() === "") continue;
    const restored = setAnnotationOnPassageInEditor(tiptap, annotationId, { passage });
    if (restored) return true;
  }
  return false;
}

export function canUndoCommentInEditor(tiptap, comment, options = {}) {
  if (!tiptap?.state?.doc) return { canUndo: false, reason: UNDO_FAILURE_REASON.MISSING_EDITOR };
  const annotationId = comment?.annotationId;
  if (!annotationId) return { canUndo: false, reason: UNDO_FAILURE_REASON.DOCUMENT_CHANGED };
  if (getAnnotationRangeInEditor(tiptap, annotationId)) return { canUndo: true };

  const suggestion = typeof comment?.suggestion === "string" ? comment.suggestion : "";
  if (suggestion) {
    const suggestionRange = findPassageMatchRange(tiptap.state.doc, suggestion);
    if (suggestionRange) return { canUndo: true };
  }

  const snapshotPassage = typeof options.snapshotPassage === "string" ? options.snapshotPassage : "";
  const original = typeof comment?.original === "string" ? comment.original : "";
  const canRestorePassage = [snapshotPassage, original].some((candidate) => {
    if (!candidate) return false;
    return Boolean(findPassageMatchRange(tiptap.state.doc, candidate));
  });
  return canRestorePassage
    ? { canUndo: true }
    : { canUndo: false, reason: UNDO_FAILURE_REASON.DOCUMENT_CHANGED };
}

export function undoCommentInEditor(tiptap, comment, options = {}) {
  if (!tiptap?.state?.doc) return { success: false, reason: UNDO_FAILURE_REASON.MISSING_EDITOR };
  const annotationId = comment?.annotationId;
  if (!annotationId) return { success: false, reason: UNDO_FAILURE_REASON.DOCUMENT_CHANGED };

  // If annotation still exists, no editor mutation is needed.
  if (getAnnotationRangeInEditor(tiptap, annotationId)) return { success: true };

  const suggestion = typeof comment?.suggestion === "string" ? comment.suggestion : "";
  const original = typeof comment?.original === "string" ? comment.original : "";
  if (suggestion && original) {
    const suggestionRange = findPassageMatchRange(tiptap.state.doc, suggestion);
    if (suggestionRange) {
      applySuggestionToPassageInEditor(tiptap, original, { passage: suggestion });
      const reannotated = setAnnotationOnPassageInEditor(tiptap, annotationId, { passage: original });
      if (reannotated) return { success: true };
    }
  }

  const snapshotPassage = typeof options.snapshotPassage === "string" ? options.snapshotPassage : "";
  const restored = reopenUsingPassages(tiptap, annotationId, [snapshotPassage, original]);
  if (restored) return { success: true };

  return { success: false, reason: UNDO_FAILURE_REASON.DOCUMENT_CHANGED };
}
